'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import { isFilled } from '@prismicio/helpers';
import { formatLink, formatTarget } from '@/app/lib/utils';
import { Settings } from '@/app/lib/settings';
import { ConditionalLink } from '@/app/lib/ConditionalLink'; // Import ConditionalLink
import Chevron from '@/app/assets/images/icon_chevron.svg';
import SmallClose from '@/app/assets/images/icon_small_close.svg';
import styles from './eventNavigation.module.scss';

export function EventNavigation({ pageData }) {
	const [showMenu, setShowMenu] = useState(false);
	const [subMenuOpen, setSubMenuOpen] = useState(null);
	const [hasHover, setHasHover] = useState(false);
	const overlayRef = useRef();

	// Manage body scroll lock when menu is toggled
	useEffect(() => {
		const body = document.querySelector('body');
		if (showMenu) {
			body.classList.add('no-scroll');
		} else {
			body.classList.remove('no-scroll');
		}
		return () => {
			body.classList.remove('no-scroll');
		};
	}, [showMenu]);

	// New useEffect to detect hover support
	useEffect(() => {
		const checkHover = () => {
			// Check if the device has hover capability
			setHasHover(window.matchMedia('(hover: hover) and (pointer: fine)').matches);
		};

		if (typeof window !== 'undefined') {
			checkHover();
			window.addEventListener('resize', checkHover);
			return () => {
				window.removeEventListener('resize', checkHover);
			};
		}
	}, []);

	// Toggle menu state
	const toggleMenu = useCallback(() => {
		setShowMenu((prev) => !prev);
		if (showMenu) setSubMenuOpen(null); // Close submenu when main menu closes
	}, [showMenu]);

	const closeMenu = (event) => {
		if (!overlayRef.current.contains(event.target)) setShowMenu(false);
	};

	const closeMenuAndResetSubmenu = useCallback(() => {
		setShowMenu(false);
		setSubMenuOpen(null);
	}, []);

	const handleMenuClick = (index, event) => {
		if (hasHover) return;

		// Prevent navigation on submenu clicks
		event.preventDefault();
		setSubMenuOpen(subMenuOpen === index ? null : index);
	};

	if (!pageData?.data?.slices6?.length) return null;

	const is2027 = pageData?.data?.version === '2027';

	return (
		<div className={clsx(styles.wrapper, styles[`version-${pageData?.data?.version || '2026'}`], showMenu && styles.visible, !showMenu && styles.hiding,
			pageData?.data?.logo === 'On Air Fest 10' && styles.oaf10,
			pageData?.data?.logo === 'On Air Fest 10 Pink' && styles.oaf10,
			pageData?.data?.logo === 'On Air Fest 10 - Desktop only' && styles.oaf10,
			(!isFilled.keyText(pageData?.data?.event_title_line_1) && !isFilled.keyText(pageData?.data?.event_title_line_2) && styles.noTitle)
		)}>

			{is2027 && (
				<Link
					href={formatLink(`${Settings.eventsPrefix}/${pageData.uid}`)}
					className={clsx(styles.event_details, styles.event_details_header)}
					onClick={closeMenuAndResetSubmenu}
				>
					{isFilled.keyText(pageData?.data?.event_title_line_1) && <p className={clsx(styles.line_1)}>{pageData?.data?.event_title_line_1}</p>}
					{isFilled.keyText(pageData?.data?.event_title_line_2) && <p className={clsx(styles.line_2)}>{pageData?.data?.event_title_line_2}</p>}
				</Link>
			)}

			<div onClick={toggleMenu} className={clsx(styles.toggle)}>
				<span>{isFilled.keyText(pageData?.data?.mobile_menu_label) ? pageData?.data?.mobile_menu_label : "Event info"}</span>
				<Chevron className={clsx(styles.chevron)} />
			</div>

			<div className={clsx(styles.overlay)} onClick={closeMenu}>
				<div className={clsx(styles.overlayContainer)} ref={overlayRef}>

					<button
						type="button"
						className={clsx(styles.close)}
						onClick={(event) => {
							event.stopPropagation();
							closeMenuAndResetSubmenu();
						}}
						aria-label="Close menu"
					>
						<SmallClose />
					</button>

					<Link
						href={formatLink(`${Settings.eventsPrefix}/${pageData.uid}`)}
						className={clsx(styles.event_details)}
						onClick={closeMenuAndResetSubmenu}
					>
						{isFilled.keyText(pageData?.data?.event_title_line_1) && <p className={clsx(styles.line_1)}>{pageData?.data?.event_title_line_1}</p>}
						{isFilled.keyText(pageData?.data?.event_title_line_2) && <p className={clsx(styles.line_2)}>{pageData?.data?.event_title_line_2}</p>}
					</Link>

					<nav className={clsx(styles.nav)}>
						<div className={clsx(styles.top)}>

							<ul className={clsx(styles.list)}>
								{pageData?.data?.slices6?.map((item, index) => {

									if (
										!isFilled.keyText(item?.primary?.anchor_title) &&
										!isFilled.link(item?.primary?.menu_item) &&
										!isFilled.keyText(item?.primary?.squadup_event_id) && // Check for squadup_event_id
										item?.primary?.sub_menu?.length === 0
									) {
										return null;
									}

									const hasSubmenu = item?.primary?.sub_menu?.length > 0;
									const isAnchorLink = isFilled.keyText(item?.primary?.anchor_title);
									const isExternalPrismicLink = isFilled.link(item?.primary?.menu_item) && !isAnchorLink; // Explicit Prismic link or SquadUp
									const itemStyle = {
										...(item?.primary?.background_color && { '--nav-bg': item.primary.background_color }),
										...(item?.primary?.text_color && { '--nav-text': item.primary.text_color }),
										...(item?.primary?.text_hover_color && { '--nav-text-hover': item.primary.text_hover_color }),
										...(item?.primary?.background_hover_color && { '--nav-bg-hover': item.primary.background_hover_color }),
									};
									const itemLinkClassName = clsx(styles.link, {
										[styles.active]: subMenuOpen === index,
										[styles.hasHoverBg]: Boolean(item?.primary?.background_hover_color),
									});

									const linkContent = (
										<>
											<span className={clsx(styles.hoverBg)} />
											<span className={clsx(styles.hoverLink)}>
												{isAnchorLink ? (item?.primary?.menu_item?.text || item?.primary?.anchor_title) : (item?.primary?.menu_item?.text)}
												{hasSubmenu ? <Chevron className={clsx(styles.chevron)} /> : null}
											</span>
										</>
									);

									// Define onClick handler for the menu item
									const itemOnClickHandler = hasSubmenu
										? (event) => handleMenuClick(index, event)
										: closeMenuAndResetSubmenu;

									// Define href/url based on content type
									let href = isAnchorLink ? `#${item?.primary?.anchor_title}` : item?.primary?.menu_item;

									return (
										<li className={clsx(styles.li)} key={index} style={itemStyle}>
											{(isAnchorLink || isExternalPrismicLink || isFilled.keyText(item?.primary?.squadup_event_id)) ? (
												// Use ConditionalLink for all links/actions that aren't just a span trigger
												<ConditionalLink
													url={href}
													squadupeventid={item?.primary?.squadup_event_id} // Pass squadup_event_id
													className={itemLinkClassName}
													onClick={itemOnClickHandler}
													data-is-submenu-trigger={hasSubmenu ? "true" : undefined}
													// Only set target if it's not an internal anchor link (handled by ConditionalLink or Link)
													target={isExternalPrismicLink && formatTarget(item?.primary?.menu_item)}
												>
													{linkContent}
												</ConditionalLink>

											) : (item?.primary?.menu_item?.text && hasSubmenu) ? (
												// Fallback to a plain SPAN if it's only a title with a submenu and no link/anchor/squadup ID
												<span
													className={itemLinkClassName}
													onClick={(event) => handleMenuClick(index, event)}
													data-is-submenu-trigger={"true"}
												>
													{linkContent}
												</span>
											) : null}

											{/* submenu */}
											{hasSubmenu ? (
												<div className={clsx(styles.bottom, { [styles.active]: subMenuOpen === index })}>

													{(isAnchorLink) ? (
														// Anchor link for the submenu title
														<Link
															href={`#${item?.primary?.anchor_title}`}
															className={clsx(styles.link, styles[item?.primary?.menu_item?.variant])}
															onClick={closeMenuAndResetSubmenu}
														>
															<span className={clsx(styles.span)}>{item?.primary?.menu_item?.text ? item?.primary?.menu_item?.text : item?.primary?.anchor_title}</span>
														</Link>

													) : (isExternalPrismicLink || isFilled.keyText(item?.primary?.squadup_event_id)) ? (
														// Prismic/SquadUp link for the submenu title
														<ConditionalLink
															url={item?.primary?.menu_item}
															squadupeventid={item?.primary?.squadup_event_id} // Pass squadup_event_id
															className={clsx(styles.link, styles[item?.primary?.menu_item?.variant])}
															onClick={closeMenuAndResetSubmenu}
															target={formatTarget(item?.primary?.menu_item)}
														>
															<span className={clsx(styles.span)}>{item?.primary?.menu_item?.text}</span>
														</ConditionalLink>

													) : (item?.primary?.menu_item?.text && hasSubmenu) ? (
														// Plain span for the submenu title
														<span
															className={clsx(styles.link, styles[item?.primary?.menu_item?.variant])}
															onClick={closeMenuAndResetSubmenu}
														>
															<span className={clsx(styles.span)}>{item?.primary?.menu_item?.text}</span>
														</span>
													) : null}

													<ul className={styles.submenu_list}>
														{item?.primary?.sub_menu.map((subItem, subIndex) => {

															if (!isFilled.keyText(subItem?.anchor_title) && !isFilled.link(subItem?.sub_menu_item) && !isFilled.keyText(subItem?.squadup_event_id)) {
																return null;
															}

															const isSubAnchorLink = isFilled.keyText(subItem?.anchor_title);
															const subHref = isSubAnchorLink ? `#${subItem?.anchor_title}` : subItem?.sub_menu_item;

															return (
																<li className={clsx(styles.li)} key={subIndex}>
																	{(isSubAnchorLink) ? (
																		// Anchor Link
																		<Link
																			href={subHref}
																			className={styles.sub_link}
																			onClick={closeMenuAndResetSubmenu}
																		>
																			{subItem?.sub_menu_item?.text ? subItem?.sub_menu_item?.text : subItem?.anchor_title}
																		</Link>

																	) : (isFilled.link(subItem?.sub_menu_item) || isFilled.keyText(subItem?.squadup_event_id)) ? (
																		// Conditional Link (Prismic or SquadUp)
																		<ConditionalLink
																			url={subHref}
																			squadupeventid={subItem?.squadup_event_id} // Pass squadup_event_id
																			className={styles.sub_link}
																			onClick={closeMenuAndResetSubmenu}
																			target={formatTarget(subItem?.sub_menu_item)}
																		>
																			{subItem?.sub_menu_item?.text}
																		</ConditionalLink>

																	) : null}
																</li>
															)
														})}
													</ul>
												</div>
											) : null}
										</li>
									);
								})}
							</ul>

						</div>
					</nav>
				</div>
			</div>
		</div>
	);
}